import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/config';

// Analytics service for tracking user interactions and business metrics
class AnalyticsService {
  constructor() {
    this.analytics = analytics;
  }

  // Check if analytics is available
  isAvailable() {
    return this.analytics !== null;
  }

  // Set user ID for analytics
  setUserId(userId) {
    if (this.isAvailable() && userId) {
      setUserId(this.analytics, userId);
    }
  }

  // Set user properties
  setUserProperties(properties) {
    if (this.isAvailable() && properties) {
      setUserProperties(this.analytics, properties);
    }
  }

  // Generic event logging
  logEvent(eventName, parameters = {}) {
    if (this.isAvailable()) {
      try {
        logEvent(this.analytics, eventName, parameters);
        console.log(`Analytics Event: ${eventName}`, parameters);
      } catch (error) {
        console.error('Error logging analytics event:', error);
      }
    }
  }

  // ===== PAGE VIEWS =====
  logPageView(pageName, pageTitle = null) {
    this.logEvent('page_view', {
      page_name: pageName,
      page_title: pageTitle || pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }

  // ===== AUTHENTICATION EVENTS =====
  logSignUp(method = 'email') {
    this.logEvent('sign_up', {
      method: method
    });
  }

  logLogin(method = 'email') {
    this.logEvent('login', {
      method: method
    });
  }

  logLogout() {
    this.logEvent('logout');
  }

  // ===== E-COMMERCE EVENTS =====
  logViewItem(itemId, itemName, category, price, currency = 'INR') {
    this.logEvent('view_item', {
      item_id: itemId,
      item_name: itemName,
      item_category: category,
      price: price,
      currency: currency
    });
  }

  logAddToCart(itemId, itemName, category, price, quantity = 1, currency = 'INR') {
    this.logEvent('add_to_cart', {
      item_id: itemId,
      item_name: itemName,
      item_category: category,
      price: price,
      quantity: quantity,
      currency: currency
    });
  }

  logRemoveFromCart(itemId, itemName, category, price, quantity = 1, currency = 'INR') {
    this.logEvent('remove_from_cart', {
      item_id: itemId,
      item_name: itemName,
      item_category: category,
      price: price,
      quantity: quantity,
      currency: currency
    });
  }

  logViewCart(items, value, currency = 'INR') {
    this.logEvent('view_cart', {
      items: items,
      value: value,
      currency: currency
    });
  }

  logBeginCheckout(value, currency = 'INR', items = []) {
    this.logEvent('begin_checkout', {
      value: value,
      currency: currency,
      items: items
    });
  }

  logPurchase(transactionId, value, currency = 'INR', items = []) {
    this.logEvent('purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items
    });
  }

  // ===== PET SERVICES EVENTS =====
  logGroomingBooking(bookingId, serviceType, price, currency = 'INR') {
    this.logEvent('grooming_booking', {
      booking_id: bookingId,
      service_type: serviceType,
      price: price,
      currency: currency
    });
  }

  logBoardingBooking(bookingId, duration, price, currency = 'INR') {
    this.logEvent('boarding_booking', {
      booking_id: bookingId,
      duration: duration,
      price: price,
      currency: currency
    });
  }

  logAIVetConsultation(consultationId, urgencyLevel, messageCount = 0) {
    this.logEvent('ai_vet_consultation', {
      consultation_id: consultationId,
      urgency_level: urgencyLevel,
      message_count: messageCount
    });
  }

  logVetAppointment(appointmentId, vetId, serviceType, price, currency = 'INR') {
    this.logEvent('vet_appointment', {
      appointment_id: appointmentId,
      vet_id: vetId,
      service_type: serviceType,
      price: price,
      currency: currency
    });
  }

  // ===== SEARCH EVENTS =====
  logSearch(searchTerm, resultsCount = 0) {
    this.logEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount
    });
  }

  // ===== CONTENT ENGAGEMENT =====
  logViewContent(contentType, contentId, contentName) {
    this.logEvent('view_content', {
      content_type: contentType,
      content_id: contentId,
      content_name: contentName
    });
  }

  logShare(contentType, contentId, method) {
    this.logEvent('share', {
      content_type: contentType,
      content_id: contentId,
      method: method
    });
  }

  // ===== ADMIN EVENTS =====
  logAdminAction(action, adminRole, targetType = null, targetId = null) {
    this.logEvent('admin_action', {
      action: action,
      admin_role: adminRole,
      target_type: targetType,
      target_id: targetId
    });
  }

  logStoreAction(action, storeId, details = {}) {
    this.logEvent('store_action', {
      action: action,
      store_id: storeId,
      ...details
    });
  }

  // ===== CUSTOM BUSINESS EVENTS =====
  logPetAdded(petType, petBreed, petAge) {
    this.logEvent('pet_added', {
      pet_type: petType,
      pet_breed: petBreed,
      pet_age: petAge
    });
  }

  logContactFormSubmission(formType, subject) {
    this.logEvent('contact_form_submission', {
      form_type: formType,
      subject: subject
    });
  }

  logVoucherUsed(voucherCode, discountAmount, currency = 'INR') {
    this.logEvent('voucher_used', {
      voucher_code: voucherCode,
      discount_amount: discountAmount,
      currency: currency
    });
  }

  logReviewSubmitted(rating, reviewType, itemId) {
    this.logEvent('review_submitted', {
      rating: rating,
      review_type: reviewType,
      item_id: itemId
    });
  }

  // ===== ERROR TRACKING =====
  logError(errorType, errorMessage, errorCode = null) {
    this.logEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      error_code: errorCode
    });
  }

  // ===== PERFORMANCE TRACKING =====
  logPerformance(metricName, value, unit = 'ms') {
    this.logEvent('performance_metric', {
      metric_name: metricName,
      value: value,
      unit: unit
    });
  }

  // ===== USER ENGAGEMENT =====
  logUserEngagement(action, duration = null, details = {}) {
    this.logEvent('user_engagement', {
      action: action,
      duration: duration,
      ...details
    });
  }

  // ===== CONVERSION TRACKING =====
  logConversion(conversionType, value = null, currency = 'INR') {
    this.logEvent('conversion', {
      conversion_type: conversionType,
      value: value,
      currency: currency
    });
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
